import React, { useRef, useState, useEffect, useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StaticPageLayout from "../../components/StaticPageLayout";
import { QuoteModal } from "../../components/QuoteModal";
import { ZipQuoteForm } from "../Home";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement | null>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

type AccentThemeT = {
  gradient: string;
  solid: string;
  glowStrong: string;
  glowSoft: string;
};

const BLUE_CYAN_ACCENT: AccentThemeT = {
  gradient: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 60%, #67e8f9 100%)",
  solid: "#38bdf8",
  glowStrong: "rgba(56,189,248,0.22)",
  glowSoft: "rgba(59,130,246,0.10)",
};

const AMBER_ACCENT: AccentThemeT = {
  gradient: "linear-gradient(90deg, #f59e0b 0%, #fbbf24 55%, #fde68a 100%)",
  solid: "#fbbf24",
  glowStrong: "rgba(251,191,36,0.22)",
  glowSoft: "rgba(245,158,11,0.10)",
};

const TEAL_ACCENT: AccentThemeT = {
  gradient: "linear-gradient(90deg, #10b981 0%, #2dd4bf 55%, #5eead4 100%)",
  solid: "#2dd4bf",
  glowStrong: "rgba(45,212,191,0.22)",
  glowSoft: "rgba(16,185,129,0.10)",
};

const GOLD_ORANGE_ACCENT: AccentThemeT = {
  gradient: "linear-gradient(90deg, #f59e0b 0%, #fb923c 50%, #fcd34d 100%)",
  solid: "#fb923c",
  glowStrong: "rgba(251,146,60,0.30)",
  glowSoft: "rgba(245,158,11,0.12)",
};

type EnergyStatT = {
  value: string;
  unit?: string;
  label: string;
};

type EnergyProductT = {
  index: string;
  name: string;
  headlineWhite: string;
  headlineAccent: string;
  tagline: string;
  description: string;
  features: string[];
  stats: EnergyStatT[];
  image: string;
  accent: AccentThemeT;
  mobileFocal?: string;
};

type SectionEdgeT = {
  top: string;
  bottom: string;
};

const SECTION_EDGES: SectionEdgeT[] = [
  { top: "#0a0f1e", bottom: "#0a0f1e" },
  { top: "#0a0f1e", bottom: "#0a0f1e" },
];

type HeroSectionPropsT = {
  onQuoteOpen: (zip: string) => void;
  t: TranslateFnT;
};

const HeroSection = ({ onQuoteOpen, t }: HeroSectionPropsT) => {
  const { ref, inView } = useInView();
  const stagger = (n: number) => `${n * 0.1}s`;

  return (
    <Box ref={ref} sx={styles.heroOuter}>
      <Box
        component="video"
        autoPlay
        loop
        muted
        playsInline
        poster="/img/assets/House-EnergyStorage-hero.jpg"
        src="/img/assets/EnergyStorage-videohero.mp4"
        sx={styles.heroVideo}
      />
      <Box sx={styles.heroVideoOverlay} />
      <Box
        sx={{
          ...styles.heroTopGlow,
          background: `radial-gradient(circle, ${BLUE_CYAN_ACCENT.glowStrong} 0%, transparent 70%)`,
        }}
      />
      <Box sx={styles.heroBottomRightFade} />
      <Container maxWidth="lg" sx={styles.heroContainer}>
        <Box sx={styles.heroCopyBox}>
          <Typography
            sx={{
              ...styles.heroEyebrow,
              color: BLUE_CYAN_ACCENT.solid,
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transition: `opacity 0.6s ease ${stagger(0)}, transform 0.6s ease ${stagger(0)}`,
            }}
          >
            {t("hero.eyebrow")}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              ...styles.heroTitle,
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transition: `opacity 0.6s ease ${stagger(1)}, transform 0.6s ease ${stagger(1)}`,
            }}
          >
            {t("hero.headlineLine1")}
            <br />
            {t("hero.headlineLine2White")}
            <Box component="span" sx={styles.accentGradientText(BLUE_CYAN_ACCENT.gradient)}>
              {t("hero.headlineAccentTop")}
              <br />
              {t("hero.headlineAccentBottom")}
            </Box>
          </Typography>
          <Typography
            sx={{
              ...styles.heroSubtitle,
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transition: `opacity 0.6s ease ${stagger(2)}, transform 0.6s ease ${stagger(2)}`,
            }}
          >
            {t("hero.subtitle")}
          </Typography>
          <Box
            sx={{
              ...styles.heroCtaRow,
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transition: `opacity 0.6s ease ${stagger(3)}, transform 0.6s ease ${stagger(3)}`,
            }}
          >
            <ZipQuoteForm ctaLabel={t("hero.ctaLabel")} onSubmit={onQuoteOpen} />
          </Box>
        </Box>
        <Box
          component="aside"
          sx={{
            ...styles.heroIncentiveAside,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(4)}, transform 0.6s ease ${stagger(4)}`,
          }}
        >
          <Box sx={styles.heroIncentiveEyebrowRow}>
            <Box
              sx={{
                ...styles.heroIncentivePulseDot,
                background: GOLD_ORANGE_ACCENT.solid,
                boxShadow: `0 0 10px ${GOLD_ORANGE_ACCENT.solid}`,
              }}
            />
            <Typography component="span" sx={{ ...styles.heroIncentiveEyebrowText, color: GOLD_ORANGE_ACCENT.solid }}>
              {t("hero.incentiveCard.eyebrow")}
            </Typography>
          </Box>
          <Box sx={styles.heroIncentiveValueRow}>
            <Box component="span" sx={styles.heroIncentivePrefixSuffix}>
              {t("hero.incentiveCard.prefix")}
            </Box>
            <Box
              component="span"
              sx={{ ...styles.heroIncentiveValueSize, ...styles.accentGradientText(GOLD_ORANGE_ACCENT.gradient) }}
            >
              {t("hero.incentiveCard.value")}
            </Box>
            <Box component="span" sx={styles.heroIncentivePrefixSuffix}>
              {t("hero.incentiveCard.suffix")}
            </Box>
          </Box>
          <Typography sx={styles.heroIncentiveNote}>
            {t("hero.incentiveCard.noteLine1")}
            <br />
            {t("hero.incentiveCard.noteLine2")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

type EnergyCopyPropsT = {
  product: EnergyProductT;
  seeSavingsLabel: string;
  ctaLabel: string;
  onQuoteOpen: (zip?: string) => void;
};

const EnergyCopy = ({ product, seeSavingsLabel, ctaLabel, onQuoteOpen }: EnergyCopyPropsT) => {
  const { ref, inView } = useInView();
  const stagger = (n: number) => `${n * 0.1}s`;
  const [zip, setZip] = useState("");

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <Box
        sx={{
          ...styles.energyCopyIndexWatermark,
          ...styles.accentGradientText(product.accent.gradient),
        }}
      >
        {product.index}
      </Box>

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            ...styles.energyCopyEyebrowRow,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(0)}, transform 0.6s ease ${stagger(0)}`,
          }}
        >
          <Typography sx={{ ...styles.energyCopyEyebrowText, color: product.accent.solid }}>
            {product.index} — {product.name.toUpperCase()}
          </Typography>
        </Box>

        <Typography
          variant="h2"
          sx={{
            ...styles.energyCopyHeadline,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(1)}, transform 0.6s ease ${stagger(1)}`,
          }}
        >
          {product.headlineWhite}
          <Box component="span" sx={styles.accentGradientText(product.accent.gradient)}>
            {product.headlineAccent}
          </Box>
        </Typography>

        <Typography
          sx={{
            ...styles.energyCopyTagline,
            color: product.accent.solid,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(2)}, transform 0.6s ease ${stagger(2)}`,
          }}
        >
          {product.tagline}
        </Typography>

        <Typography
          sx={{
            ...styles.energyCopyDescription,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(3)}, transform 0.6s ease ${stagger(3)}`,
          }}
        >
          {product.description}
        </Typography>

        <Box
          sx={{
            ...styles.energyCopyFeaturesWrap,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(4)}, transform 0.6s ease ${stagger(4)}`,
          }}
        >
          {product.features.map((feature) => (
            <Box key={feature} sx={styles.energyCopyFeatureRow}>
              <CheckCircleOutlineIcon sx={{ ...styles.energyCopyFeatureIconBase, color: product.accent.solid }} />
              <Typography sx={styles.energyCopyFeatureText}>{feature}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            ...styles.energyCopyStatsRow,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(5)}, transform 0.6s ease ${stagger(5)}`,
          }}
        >
          {product.stats.map((stat) => (
            <Box key={stat.label}>
              <Typography sx={{ ...styles.energyCopyStatValue, ...styles.accentGradientText(product.accent.gradient) }}>
                {stat.value}
                {stat.unit && (
                  <Box component="span" sx={styles.energyCopyStatUnit}>
                    {stat.unit}
                  </Box>
                )}
              </Typography>
              <Typography sx={styles.energyCopyStatLabel}>{stat.label}</Typography>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            ...styles.energyCopySeeSavingsLabel,
            color: product.accent.solid,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(6)}, transform 0.6s ease ${stagger(6)}`,
          }}
        >
          {seeSavingsLabel}
        </Typography>
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            onQuoteOpen(zip);
          }}
          sx={{
            ...styles.energyCopyFormWrap,
            border: `1.5px solid ${product.accent.solid}66`,
            boxShadow: `0 10px 30px ${product.accent.glowStrong}, inset 0 0 0 1px rgba(255,255,255,0.05)`,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 0.6s ease ${stagger(7)}, transform 0.6s ease ${stagger(7)}, box-shadow 0.3s ease`,
            "&:focus-within": {
              boxShadow: `0 12px 36px ${product.accent.glowStrong}, inset 0 0 0 1.5px ${product.accent.solid}99`,
            },
          }}
        >
          <Box
            component="input"
            type="text"
            inputMode="numeric"
            placeholder="Enter Zip Code..."
            value={zip}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setZip(e.target.value)}
            maxLength={5}
            sx={styles.energyCopyInput}
          />
          <Box
            component="button"
            type="submit"
            sx={{ ...styles.energyCopyButtonBase, background: product.accent.gradient }}
          >
            {ctaLabel}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type EnergyBackgroundImagePropsT = {
  product: EnergyProductT;
};

const EnergyBackgroundImage = ({ product }: EnergyBackgroundImagePropsT) => {
  const { ref, inView } = useInView();

  return (
    <Box ref={ref} sx={styles.energyBgWrap}>
      <Box
        component="img"
        src={product.image}
        alt={product.name}
        sx={{
          ...styles.energyBgImage,
          objectPosition: { xs: product.mobileFocal ?? "center", md: "center" },
          transform: inView ? "scale(1)" : "scale(1.08)",
        }}
      />
    </Box>
  );
};

type MobileImageBandPropsT = {
  bottomColor: string;
  children: React.ReactNode;
};

const MobileImageBand = ({ bottomColor, children }: MobileImageBandPropsT) => (
  <Box sx={styles.mobileBandWrap}>
    {children}
    <Box sx={{ ...styles.mobileBandFadeBase, background: `linear-gradient(to bottom, transparent, ${bottomColor})` }} />
  </Box>
);

type EnergySectionPropsT = {
  product: EnergyProductT;
  index: number;
  seeSavingsLabel: string;
  ctaLabel: string;
  onQuoteOpen: (zip?: string) => void;
};

const EnergySection = ({ product, index, seeSavingsLabel, ctaLabel, onQuoteOpen }: EnergySectionPropsT) => {
  const copyOnLeft = index % 2 === 0;
  const edge = SECTION_EDGES[index % SECTION_EDGES.length];

  return (
    <Box sx={styles.energySectionOuter}>
      <Box sx={styles.energySectionDesktopWrap}>
        <EnergyBackgroundImage product={product} />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            backgroundImage: {
              xs: styles.mobileScrim,
              md: copyOnLeft ? styles.scrimGradient("right") : styles.scrimGradient("left"),
            },
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "8%",
            bottom: "8%",
            width: { xs: "100%", md: "55%" },
            left: copyOnLeft ? 0 : "auto",
            right: copyOnLeft ? "auto" : 0,
            zIndex: 1,
            pointerEvents: "none",
            background: `radial-gradient(ellipse 70% 60% at ${copyOnLeft ? "25%" : "75%"} 50%, ${
              product.accent.glowStrong
            } 0%, transparent 70%)`,
            filter: "blur(70px)",
          }}
        />

        <Box
          sx={{
            ...styles.energySectionEdgeBase,
            top: 0,
            background: `linear-gradient(to bottom, ${edge.top}, transparent)`,
          }}
        />
        <Box
          sx={{
            ...styles.energySectionEdgeBase,
            bottom: 0,
            background: `linear-gradient(to top, ${edge.bottom}, transparent)`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Box
            sx={{
              maxWidth: { xs: "100%", md: "48%" },
              ml: { md: copyOnLeft ? 0 : "auto" },
              mr: { md: copyOnLeft ? "auto" : 0 },
            }}
          >
            <EnergyCopy
              product={product}
              seeSavingsLabel={seeSavingsLabel}
              ctaLabel={ctaLabel}
              onQuoteOpen={onQuoteOpen}
            />
          </Box>
        </Container>
      </Box>

      <Box sx={styles.energySectionMobileWrap}>
        <MobileImageBand bottomColor={edge.bottom}>
          <EnergyBackgroundImage product={product} />
        </MobileImageBand>

        <Box sx={{ ...styles.energySectionMobileCopyWrap, background: edge.bottom }}>
          <EnergyCopy
            product={product}
            seeSavingsLabel={seeSavingsLabel}
            ctaLabel={ctaLabel}
            onQuoteOpen={onQuoteOpen}
          />
        </Box>
      </Box>
    </Box>
  );
};

const TONE_SOLID = {
  blue: "#4a9cff",
  cyan: "#2dd4ee",
  gold: "#f4c751",
  danger: "#f87171",
  success: "#34d399",
};

const TONE_GLOW = {
  blue: "rgba(74,156,255,0.28)",
  cyan: "rgba(45,212,238,0.24)",
  gold: "rgba(244,199,81,0.24)",
  danger: "rgba(248,113,113,0.22)",
  success: "rgba(52,211,153,0.24)",
};

type CardGlowPropsT = {
  color: string;
};

const CardGlow = ({ color }: CardGlowPropsT) => (
  <Box sx={{ ...styles.cardGlow, background: `radial-gradient(circle, ${color} 0%, transparent 70%)` }} />
);

type SectionHeadingPropsT = {
  eyebrow: string;
  headingWhite: string;
  headingAccent: string;
  accent: AccentThemeT;
  sub?: React.ReactNode;
  maxWidth?: number;
};

const SectionHeading = ({
  eyebrow,
  headingWhite,
  headingAccent,
  accent,
  sub,
  maxWidth = 780,
}: SectionHeadingPropsT) => {
  const { ref, inView } = useInView();

  return (
    <Box ref={ref} sx={{ ...styles.sectionHeadingWrap, maxWidth }}>
      <Typography
        sx={{
          ...styles.sectionHeadingEyebrow,
          color: accent.solid,
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(24px)",
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          ...styles.sectionHeadingTitle,
          mb: sub ? 2.5 : 0,
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(24px)",
        }}
      >
        {headingWhite}
        <Box component="span" sx={styles.accentGradientText(accent.gradient)}>
          {headingAccent}
        </Box>
      </Typography>
      {sub && (
        <Typography
          sx={{
            ...styles.sectionHeadingSub,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
};

type CrisisCardT = {
  kicker: string;
  word: string;
  toneSolid: string;
  toneGlow: string;
  body: string;
};

type CrisisCardPropsT = {
  card: CrisisCardT;
  delay: number;
};

const CrisisCard = ({ card, delay }: CrisisCardPropsT) => {
  const { ref, inView } = useInView();

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.premiumCard,
        borderRadius: "18px",
        p: { xs: 3, md: 4 },
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      <CardGlow color={card.toneGlow} />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography sx={styles.crisisCardKicker}>{card.kicker}</Typography>
        <Typography sx={{ ...styles.crisisCardWord, color: card.toneSolid }}>{card.word}</Typography>
        <Typography sx={styles.crisisCardBody}>{card.body}</Typography>
      </Box>
    </Box>
  );
};

type CrisisSectionPropsT = {
  t: TranslateFnT;
  cards: CrisisCardT[];
};

const CrisisSection = ({ t, cards }: CrisisSectionPropsT) => (
  <Box sx={styles.darkSectionOuter}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow={t("crisis.eyebrow")}
        headingWhite={t("crisis.headingWhite")}
        headingAccent={t("crisis.headingAccent")}
        accent={BLUE_CYAN_ACCENT}
      />
      <Box sx={styles.cardsGrid3}>
        {cards.map((card, i) => (
          <CrisisCard key={card.kicker} card={card} delay={i * 0.1} />
        ))}
      </Box>
    </Container>
  </Box>
);

type SmartResponseSectionPropsT = {
  t: TranslateFnT;
};

const SmartResponseSection = ({ t }: SmartResponseSectionPropsT) => (
  <Box sx={styles.darkSectionOuter}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow={t("smartResponse.eyebrow")}
        headingWhite={t("smartResponse.headingWhite")}
        headingAccent={t("smartResponse.headingAccent")}
        accent={BLUE_CYAN_ACCENT}
        sub={
          <>
            {t("smartResponse.subLine1")}
            <br />
            {t("smartResponse.subLine2")}
          </>
        }
        maxWidth={1120}
      />
    </Container>
  </Box>
);

const ARBITRAGE_COLORS = {
  bgPage: "#05070d",
  bgBase: "#0a0e18",
  bgSurface: "#11162a",
  bgElevated: "#161c34",
  bgElevated2: "#1d2542",
  border: "rgba(255,255,255,0.06)",
  borderStrong: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textSoft: "rgba(255,255,255,0.88)",
  textMuted: "rgba(255,255,255,0.55)",
  textDim: "rgba(255,255,255,0.32)",
  accent: "#4a9cff",
  accentSoft: "rgba(74,156,255,0.14)",
  cyan: "#2dd4ee",
  gold: "#f4c751",
  success: "#34d399",
  danger: "#f87171",
  gradAccent: "linear-gradient(135deg, #4a9cff 0%, #2dd4ee 100%)",
} as const;

const ARBITRAGE_ACCENT: AccentThemeT = {
  gradient: ARBITRAGE_COLORS.gradAccent,
  solid: ARBITRAGE_COLORS.accent,
  glowStrong: ARBITRAGE_COLORS.accentSoft,
  glowSoft: ARBITRAGE_COLORS.accentSoft,
};

type ArbitrageIconKeyT = "moon" | "clock";

type ArbitrageInfoCardT = {
  title: string;
  desc: string;
  icon: ArbitrageIconKeyT;
  iconColor: string;
};

type ArbitrageInfoCardPropsT = {
  card: ArbitrageInfoCardT;
  delay: number;
};

const ArbitrageInfoCard = ({ card, delay }: ArbitrageInfoCardPropsT) => {
  const { ref, inView } = useInView();
  const IconComponent = card.icon === "moon" ? NightsStayOutlinedIcon : AccessTimeOutlinedIcon;

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.arbitrageInfoCard,
        background: ARBITRAGE_COLORS.bgElevated2,
        border: `1px solid ${ARBITRAGE_COLORS.border}`,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      <Box
        sx={{
          ...styles.arbitrageInfoIconWrap,
          background: ARBITRAGE_COLORS.bgSurface,
          border: `1px solid ${ARBITRAGE_COLORS.borderStrong}`,
        }}
      >
        <IconComponent sx={{ color: card.iconColor, fontSize: "1.4rem" }} />
      </Box>
      <Box>
        <Typography sx={{ ...styles.arbitrageInfoTitle, color: ARBITRAGE_COLORS.text }}>{card.title}</Typography>
        <Typography sx={{ ...styles.arbitrageInfoDesc, color: ARBITRAGE_COLORS.textMuted }}>{card.desc}</Typography>
      </Box>
    </Box>
  );
};

type SavingsDialPropsT = {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
  size?: number;
  children?: React.ReactNode;
};

const SavingsDial = ({ value, min = 80, max = 600, step = 1, onChange, size = 320, children }: SavingsDialPropsT) => {
  const dialRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const uid = useId().replace(/:/g, "");
  const idBg = `dialBg-${uid}`;
  const idArc = `dialArc-${uid}`;
  const idGlow = `dialGlow-${uid}`;
  const r = size / 2 - 32;
  const trackR = size / 2 - 32;
  const innerR = size / 2 - 50;
  const cx = size / 2;
  const cy = size / 2;
  const t = (value - min) / (max - min);
  const angle = -135 + t * 270;
  const angleRad = (angle * Math.PI) / 180;
  const handleX = cx + r * Math.sin(angleRad);
  const handleY = cy - r * Math.cos(angleRad);

  const arcPath = (a0: number, a1: number) => {
    const p0 = {
      x: cx + trackR * Math.sin((a0 * Math.PI) / 180),
      y: cy - trackR * Math.cos((a0 * Math.PI) / 180),
    };
    const p1 = {
      x: cx + trackR * Math.sin((a1 * Math.PI) / 180),
      y: cy - trackR * Math.cos((a1 * Math.PI) / 180),
    };
    const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
    const sweep = a1 > a0 ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${trackR} ${trackR} 0 ${large} ${sweep} ${p1.x} ${p1.y}`;
  };

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = dialRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const ccx = rect.left + rect.width / 2;
      const ccy = rect.top + rect.height / 2;
      const dx = clientX - ccx;
      const dy = clientY - ccy;
      let a = (Math.atan2(dx, -dy) * 180) / Math.PI;
      if (a > 135) a = 135;
      if (a < -135) a = -135;
      const nt = (a + 135) / 270;
      const raw = min + nt * (max - min);
      const snapped = Math.round(raw / step) * step;
      const clamped = Math.max(min, Math.min(max, snapped));
      if (clamped !== value) onChange(clamped);
    },
    [min, max, step, onChange, value],
  );

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e: PointerEvent) => {
      e.preventDefault();
      updateFromPointer(e.clientX, e.clientY);
    };
    const up = () => setDragging(false);
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [dragging, updateFromPointer]);

  const beginDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    setDragging(true);
    updateFromPointer(e.clientX, e.clientY);
  };

  const notches: React.ReactElement[] = [];
  const NOTCH_COUNT = 36;
  for (let i = 0; i <= NOTCH_COUNT; i++) {
    const a = -135 + (i / NOTCH_COUNT) * 270;
    const ar = (a * Math.PI) / 180;
    const inner = size / 2 - 44;
    const outer = size / 2 - 32;
    const x1 = cx + inner * Math.sin(ar);
    const y1 = cy - inner * Math.cos(ar);
    const x2 = cx + outer * Math.sin(ar);
    const y2 = cy - outer * Math.cos(ar);
    const isActive = a <= angle + 0.5;
    notches.push(
      <line
        key={i}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={isActive ? "#4a9cff" : "rgba(255,255,255,0.12)"}
        strokeWidth={i % 6 === 0 ? 2 : 1.25}
        strokeLinecap="round"
        opacity={isActive ? 1 : 0.55}
      />,
    );
  }

  return (
    <Box
      sx={{ position: "relative", width: size, height: size, userSelect: "none", touchAction: "none" }}
      ref={dialRef}
    >
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id={idBg} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#161c34" />
            <stop offset="70%" stopColor="#11162a" />
            <stop offset="100%" stopColor="#05070d" />
          </radialGradient>
          <linearGradient id={idArc} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4a9cff" />
            <stop offset="100%" stopColor="#2dd4ee" />
          </linearGradient>
          <filter id={idGlow} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx={cx}
          cy={cy}
          r={size / 2 - 12}
          fill={`url(#${idBg})`}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="1"
        />
        <circle cx={cx} cy={cy} r={innerR - 4} fill="#05070d" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        <path
          d={arcPath(-135, angle)}
          fill="none"
          stroke={`url(#${idArc})`}
          strokeWidth="3"
          strokeLinecap="round"
          opacity="0.65"
        />
        {notches}
        <text
          x={cx + (size / 2 - 16) * Math.sin((-135 * Math.PI) / 180)}
          y={cy - (size / 2 - 16) * Math.cos((-135 * Math.PI) / 180) + 12}
          fill="rgba(255,255,255,0.32)"
          fontSize="10"
          textAnchor="end"
          fontFamily="monospace"
        >
          {min}
        </text>
        <text
          x={cx + (size / 2 - 16) * Math.sin((135 * Math.PI) / 180)}
          y={cy - (size / 2 - 16) * Math.cos((135 * Math.PI) / 180) + 12}
          fill="rgba(255,255,255,0.32)"
          fontSize="10"
          textAnchor="start"
          fontFamily="monospace"
        >
          {max}
        </text>
        <g filter={`url(#${idGlow})`}>
          <circle
            cx={handleX}
            cy={handleY}
            r={dragging ? 16 : 14}
            fill="#4a9cff"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2"
            style={{ cursor: dragging ? "grabbing" : "grab", transition: dragging ? "none" : "r 150ms ease" }}
          />
        </g>
        <circle
          cx={handleX}
          cy={handleY}
          r="28"
          fill="transparent"
          onPointerDown={beginDrag}
          style={{ cursor: dragging ? "grabbing" : "grab" }}
        />
        <circle
          cx={cx}
          cy={cy}
          r={size / 2 - 12}
          fill="transparent"
          onPointerDown={beginDrag}
          style={{ cursor: "grab" }}
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          pointerEvents: "none",
          textAlign: "center",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

type ArbitrageTileT = {
  label: string;
  value: string;
  color: string;
};

const ARBITRAGE_SHIFT_FRACTION = 0.42;
const ARBITRAGE_RATE_SPREAD_PER_KWH = 0.32;

type ArbitrageCalculatorPropsT = {
  t: TranslateFnT;
};

const ArbitrageCalculator = ({ t }: ArbitrageCalculatorPropsT) => {
  const { ref, inView } = useInView();
  const [peakUsage, setPeakUsage] = useState(850);

  const shifted = Math.round(peakUsage * ARBITRAGE_SHIFT_FRACTION);
  const monthly = Math.round(shifted * ARBITRAGE_RATE_SPREAD_PER_KWH);
  const daily = (monthly / 30).toFixed(2);
  const annual = monthly * 12;

  const smallTiles: ArbitrageTileT[] = [
    {
      label: t("arbitrage.calculator.tiles.energyShifted.label"),
      value: t("arbitrage.calculator.tiles.energyShifted.value", { value: shifted }),
      color: ARBITRAGE_COLORS.accent,
    },
    {
      label: t("arbitrage.calculator.tiles.rateSpread.label"),
      value: t("arbitrage.calculator.tiles.rateSpread.value"),
      color: ARBITRAGE_COLORS.text,
    },
  ];

  const bigTiles: ArbitrageTileT[] = [
    {
      label: t("arbitrage.calculator.tiles.dailySavings.label"),
      value: t("arbitrage.calculator.tiles.dailySavings.value", { value: daily }),
      color: ARBITRAGE_COLORS.text,
    },
    {
      label: t("arbitrage.calculator.tiles.monthlySavings.label"),
      value: t("arbitrage.calculator.tiles.monthlySavings.value", { value: monthly.toLocaleString() }),
      color: ARBITRAGE_COLORS.accent,
    },
    {
      label: t("arbitrage.calculator.tiles.annualSavings.label"),
      value: t("arbitrage.calculator.tiles.annualSavings.value", { value: annual.toLocaleString() }),
      color: ARBITRAGE_COLORS.success,
    },
  ];

  return (
    <>
      <Box
        ref={ref}
        sx={{
          ...styles.arbitrageCalculatorOuter,
          background: ARBITRAGE_COLORS.bgSurface,
          border: `1px solid ${ARBITRAGE_COLORS.border}`,
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(24px)",
        }}
      >
        <Box sx={styles.arbitrageDialColumn}>
          <SavingsDial value={peakUsage} min={200} max={2000} step={10} size={300} onChange={setPeakUsage}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ ...styles.arbitrageDialCaptionLabel, color: ARBITRAGE_COLORS.textMuted }}>
                {t("arbitrage.calculator.peakUsageLabel")}
              </Typography>
              <Typography
                sx={{ ...styles.arbitrageDialCaptionValue, ...styles.accentGradientText(ARBITRAGE_COLORS.gradAccent) }}
              >
                {peakUsage.toLocaleString()}
              </Typography>
              <Typography sx={{ ...styles.arbitrageDialUnitLabel, color: ARBITRAGE_COLORS.textMuted }}>
                {t("arbitrage.calculator.kwhPerMonthLabel")}
              </Typography>
            </Box>
          </SavingsDial>
          <Typography component="div" sx={{ ...styles.arbitrageDragHintRow, color: ARBITRAGE_COLORS.textDim }}>
            <Box
              component="span"
              sx={{
                ...styles.arbitrageDragHintDot,
                background: ARBITRAGE_COLORS.accent,
                boxShadow: `0 0 8px ${ARBITRAGE_COLORS.accent}`,
              }}
            />
            {t("arbitrage.calculator.dragHint")}
          </Typography>
        </Box>
        <Box sx={styles.arbitrageTilesGrid}>
          <Box sx={styles.arbitrageSmallTilesGrid}>
            {smallTiles.map((tile) => (
              <Box
                key={tile.label}
                sx={{
                  ...styles.arbitrageSmallTile,
                  background: ARBITRAGE_COLORS.bgElevated,
                  border: `1px solid ${ARBITRAGE_COLORS.border}`,
                }}
              >
                <Typography sx={{ ...styles.arbitrageSmallTileLabel, color: ARBITRAGE_COLORS.textMuted }}>
                  {tile.label}
                </Typography>
                <Typography sx={{ ...styles.arbitrageSmallTileValue, color: tile.color }}>{tile.value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={styles.arbitrageBigTilesGrid}>
            {bigTiles.map((tile) => (
              <Box
                key={tile.label}
                sx={{
                  ...styles.arbitrageBigTile,
                  background: ARBITRAGE_COLORS.bgElevated,
                  border: `1px solid ${ARBITRAGE_COLORS.border}`,
                }}
              >
                <Typography sx={{ ...styles.arbitrageBigTileValue, color: tile.color }}>{tile.value}</Typography>
                <Typography sx={{ ...styles.arbitrageBigTileLabel, color: ARBITRAGE_COLORS.textMuted }}>
                  {tile.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ ...styles.arbitrageCalculatorDisclaimer, color: ARBITRAGE_COLORS.textDim }}>
        {t("arbitrage.calculator.disclaimer")}
      </Typography>
    </>
  );
};

const ARBITRAGE_DAILY_RATE: number[] = [
  0.28, 0.28, 0.28, 0.28, 0.28, 0.28, 0.34, 0.34, 0.34, 0.34, 0.34, 0.34, 0.4, 0.4, 0.4, 0.4, 0.6, 0.6, 0.6, 0.6, 0.6,
  0.34, 0.34, 0.34,
];

const ARBITRAGE_DAILY_BATTERY: number[] = [
  0.9, 0.9, 0.9, 0.9, 0.6, 0.3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, -1, -1, -0.9, -0.8, -0.6, 0, 0, 0,
];

const ARBITRAGE_HOURS: number[] = Array.from({ length: 24 }, (_, hour) => hour);

const ARBITRAGE_FLOW_WIDTH = 1312;
const ARBITRAGE_FLOW_HEIGHT = 300;
const ARBITRAGE_FLOW_PAD_X = 56;
const ARBITRAGE_FLOW_PAD_T = 24;
const ARBITRAGE_FLOW_PAD_B = 46;
const ARBITRAGE_FLOW_MAX_RATE = 0.66;

type ArbitrageHourLabelKeyT = "midnight" | "sixAm" | "noon" | "fourPm" | "ninePm";

type ArbitrageHourLabelDefT = {
  hour: number;
  key: ArbitrageHourLabelKeyT;
};

const ARBITRAGE_HOUR_LABEL_DEFS: ArbitrageHourLabelDefT[] = [
  { hour: 0, key: "midnight" },
  { hour: 6, key: "sixAm" },
  { hour: 12, key: "noon" },
  { hour: 16, key: "fourPm" },
  { hour: 21, key: "ninePm" },
  { hour: 23, key: "midnight" },
];

type ArbitrageDailyFlowPropsT = {
  t: TranslateFnT;
};

const ArbitrageDailyFlow = ({ t }: ArbitrageDailyFlowPropsT) => {
  const { ref, inView } = useInView();

  const innerW = ARBITRAGE_FLOW_WIDTH - ARBITRAGE_FLOW_PAD_X * 2;
  const plotH = ARBITRAGE_FLOW_HEIGHT - ARBITRAGE_FLOW_PAD_T - ARBITRAGE_FLOW_PAD_B;
  const cell = innerW / 24;
  const cx = (h: number) => ARBITRAGE_FLOW_PAD_X + (h + 0.5) * cell;
  const yRate = (rate: number) => ARBITRAGE_FLOW_PAD_T + (1 - rate / ARBITRAGE_FLOW_MAX_RATE) * plotH;
  const midY = ARBITRAGE_FLOW_PAD_T + plotH * 0.62;

  const ratePath = `M ${ARBITRAGE_FLOW_PAD_X} ${yRate(ARBITRAGE_DAILY_RATE[0])} L ${ARBITRAGE_DAILY_RATE.map(
    (rate, h) => `${cx(h)} ${yRate(rate)}`,
  ).join(" L ")} L ${ARBITRAGE_FLOW_WIDTH - ARBITRAGE_FLOW_PAD_X} ${yRate(ARBITRAGE_DAILY_RATE[23])}`;
  const rateArea = `${ratePath} L ${ARBITRAGE_FLOW_WIDTH - ARBITRAGE_FLOW_PAD_X} ${
    ARBITRAGE_FLOW_HEIGHT - ARBITRAGE_FLOW_PAD_B
  } L ${ARBITRAGE_FLOW_PAD_X} ${ARBITRAGE_FLOW_HEIGHT - ARBITRAGE_FLOW_PAD_B} Z`;

  const hourLabels = ARBITRAGE_HOUR_LABEL_DEFS.map((def) => ({
    hour: def.hour,
    label: t(`arbitrage.dailyFlow.hourLabels.${def.key}`),
  }));

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.arbitrageFlowOuter,
        background: ARBITRAGE_COLORS.bgSurface,
        border: `1px solid ${ARBITRAGE_COLORS.border}`,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
      }}
    >
      <Box sx={styles.arbitrageFlowHeaderRow}>
        <Box>
          <Typography sx={{ ...styles.arbitrageFlowEyebrow, color: ARBITRAGE_COLORS.textMuted }}>
            {t("arbitrage.dailyFlow.eyebrow")}
          </Typography>
          <Typography sx={{ ...styles.arbitrageFlowHeading, color: ARBITRAGE_COLORS.text }}>
            {t("arbitrage.dailyFlow.heading")}
          </Typography>
        </Box>
        <Box sx={styles.arbitrageFlowLegendRow}>
          <Box sx={styles.arbitrageFlowLegendItem}>
            <Box sx={{ ...styles.arbitrageFlowLegendSwatch, background: ARBITRAGE_COLORS.success }} />
            <Typography sx={{ ...styles.arbitrageFlowLegendLabel, color: ARBITRAGE_COLORS.textMuted }}>
              {t("arbitrage.dailyFlow.legend.charging")}
            </Typography>
          </Box>
          <Box sx={styles.arbitrageFlowLegendItem}>
            <Box sx={{ ...styles.arbitrageFlowLegendSwatch, background: ARBITRAGE_COLORS.accent }} />
            <Typography sx={{ ...styles.arbitrageFlowLegendLabel, color: ARBITRAGE_COLORS.textMuted }}>
              {t("arbitrage.dailyFlow.legend.discharging")}
            </Typography>
          </Box>
          <Box sx={styles.arbitrageFlowLegendItem}>
            <Box sx={{ ...styles.arbitrageFlowLegendLineSwatch, background: ARBITRAGE_COLORS.danger }} />
            <Typography sx={{ ...styles.arbitrageFlowLegendLabel, color: ARBITRAGE_COLORS.textMuted }}>
              {t("arbitrage.dailyFlow.legend.utilityRate")}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.arbitrageFlowSvgWrap}>
        <svg
          viewBox={`0 0 ${ARBITRAGE_FLOW_WIDTH} ${ARBITRAGE_FLOW_HEIGHT}`}
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <defs>
            <linearGradient id="b4flow-rate" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={ARBITRAGE_COLORS.danger} stopOpacity="0.22" />
              <stop offset="1" stopColor={ARBITRAGE_COLORS.danger} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <rect
            x={ARBITRAGE_FLOW_PAD_X + 16 * cell}
            y={ARBITRAGE_FLOW_PAD_T}
            width={5 * cell}
            height={plotH}
            fill={ARBITRAGE_COLORS.danger}
            opacity="0.07"
          />
          <text
            x={ARBITRAGE_FLOW_PAD_X + 18.5 * cell}
            y={ARBITRAGE_FLOW_PAD_T + 14}
            fill={ARBITRAGE_COLORS.danger}
            fontSize="11"
            fontWeight="600"
            textAnchor="middle"
            fontFamily="'Barlow', sans-serif"
          >
            {t("arbitrage.dailyFlow.peakLabel")}
          </text>
          <path d={rateArea} fill="url(#b4flow-rate)" />
          <path
            d={ratePath}
            fill="none"
            stroke={ARBITRAGE_COLORS.danger}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {ARBITRAGE_HOURS.map((hour) => {
            const batteryValue = ARBITRAGE_DAILY_BATTERY[hour];
            if (batteryValue === 0) return null;
            const barW = cell * 0.6;
            const full = plotH * 0.32;
            const hgt = Math.abs(batteryValue) * full;
            const isCharge = batteryValue > 0;
            const y = isCharge ? midY - hgt : midY;
            const x = cx(hour) - barW / 2;
            return (
              <rect
                key={hour}
                x={x}
                y={y}
                width={barW}
                height={hgt}
                rx="2"
                fill={isCharge ? ARBITRAGE_COLORS.success : ARBITRAGE_COLORS.accent}
                opacity="0.9"
              />
            );
          })}
          <line
            x1={ARBITRAGE_FLOW_PAD_X}
            y1={midY}
            x2={ARBITRAGE_FLOW_WIDTH - ARBITRAGE_FLOW_PAD_X}
            y2={midY}
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="1"
          />
          {hourLabels.map(({ hour, label }) => (
            <text
              key={hour}
              x={cx(hour)}
              y={ARBITRAGE_FLOW_HEIGHT - ARBITRAGE_FLOW_PAD_B + 22}
              fill="rgba(255,255,255,0.32)"
              fontSize="11"
              textAnchor="middle"
              fontFamily="'Barlow', sans-serif"
            >
              {label}
            </text>
          ))}
        </svg>
      </Box>
    </Box>
  );
};

type ArbitrageSectionPropsT = {
  t: TranslateFnT;
  infoCards: ArbitrageInfoCardT[];
};

const ArbitrageSection = ({ t, infoCards }: ArbitrageSectionPropsT) => (
  <Box sx={styles.darkSectionOuter}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow={t("arbitrage.eyebrow")}
        headingWhite={t("arbitrage.headingWhite")}
        headingAccent={t("arbitrage.headingAccent")}
        accent={ARBITRAGE_ACCENT}
        sub={t("arbitrage.subtitle")}
      />
      <Box sx={styles.arbitrageInfoCardsGrid}>
        {infoCards.map((card, i) => (
          <ArbitrageInfoCard key={card.title} card={card} delay={i * 0.1} />
        ))}
      </Box>
      <ArbitrageCalculator t={t} />
      <ArbitrageDailyFlow t={t} />
    </Container>
  </Box>
);

type BackupModeT = {
  title: string;
  pillLabel: string;
  toneSolid: string;
  toneGlow: string;
  items: string[];
};

type BackupModeCardPropsT = {
  mode: BackupModeT;
  delay: number;
};

const BackupModeCard = ({ mode, delay }: BackupModeCardPropsT) => {
  const { ref, inView } = useInView();

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.premiumCard,
        borderRadius: "20px",
        p: { xs: 3, md: 4 },
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      <CardGlow color={mode.toneGlow} />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={styles.backupModeHeaderRow}>
          <Typography sx={styles.backupModeTitle}>{mode.title}</Typography>
          <Box sx={{ ...styles.backupModePill, border: `1px solid ${mode.toneSolid}`, color: mode.toneSolid }}>
            {mode.pillLabel}
          </Box>
        </Box>
        <Box sx={styles.backupModeItemsGrid}>
          {mode.items.map((item) => (
            <Box key={item} sx={styles.backupModeItemRow}>
              <CheckCircleOutlineIcon sx={{ color: mode.toneSolid, fontSize: "1.05rem", flexShrink: 0 }} />
              <Typography sx={styles.backupModeItemText}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

type BackupModesSectionPropsT = {
  t: TranslateFnT;
  modes: BackupModeT[];
};

const BackupModesSection = ({ t, modes }: BackupModesSectionPropsT) => (
  <Box sx={styles.darkSectionOuter}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow={t("backupModes.eyebrow")}
        headingWhite={t("backupModes.headingWhite")}
        headingAccent={t("backupModes.headingAccent")}
        accent={BLUE_CYAN_ACCENT}
        sub={t("backupModes.subtitle")}
        maxWidth={1120}
      />
      <Box sx={styles.cardsGrid2}>
        {modes.map((mode, i) => (
          <BackupModeCard key={mode.title} mode={mode} delay={i * 0.1} />
        ))}
      </Box>
    </Container>
  </Box>
);

type IncentiveSectionPropsT = {
  t: TranslateFnT;
  onQuoteOpen: (zip?: string) => void;
};

const IncentiveSection = ({ t, onQuoteOpen }: IncentiveSectionPropsT) => {
  const { ref, inView } = useInView();
  const stagger = (n: number) => `${n * 0.1}s`;

  return (
    <Box sx={styles.darkSectionOuter}>
      <Container maxWidth="lg">
        <Box ref={ref} sx={{ ...styles.incentiveCardWrap, boxShadow: `0 24px 70px ${GOLD_ORANGE_ACCENT.glowStrong}` }}>
          <Box
            sx={{
              ...styles.incentiveGlowCircle,
              background: `radial-gradient(circle, ${GOLD_ORANGE_ACCENT.glowSoft} 0%, transparent 70%)`,
            }}
          />
          <Box sx={styles.incentiveBgWatermarkWrap}>
            <Box sx={{ ...styles.incentiveBgWatermarkText, ...styles.accentGradientText(GOLD_ORANGE_ACCENT.gradient) }}>
              {t("incentive.bgPercent")}
            </Box>
          </Box>

          <Box sx={styles.incentiveContent}>
            <Box
              sx={{
                ...styles.incentivePillRow,
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${stagger(0)}, transform 0.6s ease ${stagger(0)}`,
              }}
            >
              <Box
                sx={{
                  ...styles.incentivePulseDot,
                  background: GOLD_ORANGE_ACCENT.solid,
                  boxShadow: `0 0 10px ${GOLD_ORANGE_ACCENT.solid}`,
                }}
              />
              <Typography sx={{ ...styles.incentivePillText, color: GOLD_ORANGE_ACCENT.solid }}>
                {t("incentive.eyebrow")}
              </Typography>
            </Box>

            <Box
              sx={{
                ...styles.incentiveValueBlock,
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${stagger(1)}, transform 0.6s ease ${stagger(1)}`,
              }}
            >
              <Box sx={styles.incentiveValueRow}>
                <Box component="span" sx={styles.incentiveValuePrefix}>
                  {t("incentive.prefix")}
                </Box>
                <Box
                  component="span"
                  sx={{ ...styles.incentiveValueNumber, ...styles.accentGradientText(GOLD_ORANGE_ACCENT.gradient) }}
                >
                  {t("incentive.value")}
                </Box>
                <Box component="span" sx={styles.incentiveValueSuffix}>
                  {t("incentive.suffix")}
                </Box>
              </Box>
              <Typography sx={styles.incentiveValueCaption}>{t("incentive.note")}</Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                ...styles.incentiveHeadline,
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${stagger(2)}, transform 0.6s ease ${stagger(2)}`,
              }}
            >
              {t("incentive.headline")}
            </Typography>

            <Typography
              sx={{
                ...styles.incentiveBody,
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${stagger(3)}, transform 0.6s ease ${stagger(3)}`,
              }}
            >
              {t("incentive.body")}
            </Typography>

            <Box
              sx={{
                ...styles.incentiveFormRow,
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${stagger(4)}, transform 0.6s ease ${stagger(4)}`,
              }}
            >
              <ZipQuoteForm ctaLabel={t("incentive.ctaLabel")} maxWidth={420} onSubmit={onQuoteOpen} />
            </Box>

            <Typography
              sx={{
                ...styles.incentiveDisclaimer,
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${stagger(5)}, transform 0.6s ease ${stagger(5)}`,
              }}
            >
              {t("incentive.disclaimer")}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type OutcomeRowT = {
  label: string;
  value: string;
};

type OutcomeCardT = {
  title: string;
  highlighted: boolean;
  rows: OutcomeRowT[];
  noteLabel: string;
  noteText: string;
};

type OutcomeCardPropsT = {
  card: OutcomeCardT;
  accent: AccentThemeT;
  delay: number;
};

const OutcomeCard = ({ card, accent, delay }: OutcomeCardPropsT) => {
  const { ref, inView } = useInView();
  const noteColor = card.highlighted ? TEAL_ACCENT.solid : TONE_SOLID.danger;
  const noteBackground = card.highlighted ? TEAL_ACCENT.glowSoft : "rgba(248,113,113,0.08)";
  const noteBorder = card.highlighted ? "rgba(45,212,191,0.25)" : "rgba(248,113,113,0.25)";
  const valueColor = card.highlighted ? accent.solid : TONE_SOLID.danger;
  const glowColor = card.highlighted ? TONE_GLOW.blue : TONE_GLOW.danger;

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.premiumCard,
        background: card.highlighted ? accent.glowSoft : "rgba(255,255,255,0.03)",
        border: card.highlighted ? `1.5px solid ${accent.solid}` : "1px solid rgba(255,255,255,0.08)",
        borderRadius: "20px",
        p: { xs: 3, md: 4 },
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      <CardGlow color={glowColor} />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography sx={styles.outcomeCardTitle}>{card.title}</Typography>
        <Box sx={styles.outcomeRowsWrap}>
          {card.rows.map((row) => (
            <Box key={row.label} sx={styles.outcomeRow}>
              <Typography sx={styles.outcomeRowLabel}>{row.label}</Typography>
              <Typography sx={{ ...styles.outcomeRowValue, color: valueColor }}>{row.value}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ ...styles.outcomeNoteBox, background: noteBackground, border: `1px solid ${noteBorder}` }}>
          <Typography sx={{ ...styles.outcomeNoteLabel, color: noteColor }}>{card.noteLabel}</Typography>
          <Typography sx={styles.outcomeNoteText}>{card.noteText}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

type CallToActionSectionPropsT = {
  t: TranslateFnT;
  doNothingCard: OutcomeCardT;
  takeControlCard: OutcomeCardT;
  onQuoteOpen: (zip?: string) => void;
};

const CallToActionSection = ({ t, doNothingCard, takeControlCard, onQuoteOpen }: CallToActionSectionPropsT) => (
  <Box sx={styles.ctaFinalOuter}>
    <Box component="img" src="/img/assets/House-EnergyStorage-cta2.jpg" alt="" sx={styles.ctaFinalBgImage} />
    <Box sx={styles.ctaFinalOverlay} />
    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
      <SectionHeading
        eyebrow={t("cta.eyebrow")}
        headingWhite={t("cta.headingWhite")}
        headingAccent={t("cta.headingAccent")}
        accent={BLUE_CYAN_ACCENT}
      />
      <Box sx={styles.ctaFinalOutcomesGrid}>
        <OutcomeCard card={doNothingCard} accent={BLUE_CYAN_ACCENT} delay={0} />
        <OutcomeCard card={takeControlCard} accent={BLUE_CYAN_ACCENT} delay={0.1} />
      </Box>
      <Box sx={styles.ctaFinalBodyWrap}>
        <Typography sx={styles.ctaFinalBodyText}>{t("cta.body")}</Typography>
        <Box sx={styles.ctaFinalFormRow}>
          <ZipQuoteForm ctaLabel={t("cta.ctaLabel")} onSubmit={onQuoteOpen} />
        </Box>
      </Box>
    </Container>
  </Box>
);

const EnergyStorageContent = () => {
  const { t } = useTranslation("static_energy_storage");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteZip, setQuoteZip] = useState("");

  const handleQuoteOpen = (zip = "") => {
    setQuoteZip(zip);
    setQuoteOpen(true);
  };

  const energyProducts: EnergyProductT[] = [
    {
      index: t("products.tesla.index"),
      name: t("products.tesla.name"),
      headlineWhite: t("products.tesla.headlineWhite"),
      headlineAccent: t("products.tesla.headlineAccent"),
      tagline: t("products.tesla.tagline"),
      description: t("products.tesla.description"),
      features: [
        t("products.tesla.features.silentMaintenance"),
        t("products.tesla.features.instantSwitchover"),
        t("products.tesla.features.usableCapacity"),
        t("products.tesla.features.opticasterAi"),
        t("products.tesla.features.warranty"),
      ],
      stats: [
        {
          value: t("products.tesla.stats.capacity.value"),
          unit: t("products.tesla.stats.capacity.unit"),
          label: t("products.tesla.stats.capacity.label"),
        },
        {
          value: t("products.tesla.stats.power.value"),
          unit: t("products.tesla.stats.power.unit"),
          label: t("products.tesla.stats.power.label"),
        },
        {
          value: t("products.tesla.stats.efficiency.value"),
          unit: t("products.tesla.stats.efficiency.unit"),
          label: t("products.tesla.stats.efficiency.label"),
        },
      ],
      image: "/img/assets/House-prod-tesla.jpg",
      accent: BLUE_CYAN_ACCENT,
      mobileFocal: "74% center",
    },
    {
      index: t("products.enphase.index"),
      name: t("products.enphase.name"),
      headlineWhite: t("products.enphase.headlineWhite"),
      headlineAccent: t("products.enphase.headlineAccent"),
      tagline: t("products.enphase.tagline"),
      description: t("products.enphase.description"),
      features: [
        t("products.enphase.features.wholeHomeBackup"),
        t("products.enphase.features.timeOfUseSavings"),
        t("products.enphase.features.saferChemistry"),
        t("products.enphase.features.pairsWithIq8"),
        t("products.enphase.features.silentMaintenanceFree"),
      ],
      stats: [
        {
          value: t("products.enphase.stats.capacity.value"),
          unit: t("products.enphase.stats.capacity.unit"),
          label: t("products.enphase.stats.capacity.label"),
        },
        {
          value: t("products.enphase.stats.power.value"),
          unit: t("products.enphase.stats.power.unit"),
          label: t("products.enphase.stats.power.label"),
        },
        {
          value: t("products.enphase.stats.warranty.value"),
          unit: t("products.enphase.stats.warranty.unit"),
          label: t("products.enphase.stats.warranty.label"),
        },
      ],
      image: "/img/assets/House-prod-enphase2.jpg",
      accent: AMBER_ACCENT,
      mobileFocal: "74% center",
    },
  ];

  const crisisCards: CrisisCardT[] = [
    {
      kicker: t("crisis.cards.demand.kicker"),
      word: t("crisis.cards.demand.word"),
      toneSolid: TONE_SOLID.blue,
      toneGlow: TONE_GLOW.blue,
      body: t("crisis.cards.demand.body"),
    },
    {
      kicker: t("crisis.cards.infrastructure.kicker"),
      word: t("crisis.cards.infrastructure.word"),
      toneSolid: TONE_SOLID.gold,
      toneGlow: TONE_GLOW.gold,
      body: t("crisis.cards.infrastructure.body"),
    },
    {
      kicker: t("crisis.cards.outages.kicker"),
      word: t("crisis.cards.outages.word"),
      toneSolid: TONE_SOLID.danger,
      toneGlow: TONE_GLOW.danger,
      body: t("crisis.cards.outages.body"),
    },
  ];

  const arbitrageInfoCards: ArbitrageInfoCardT[] = [
    {
      title: t("arbitrage.infoCards.offPeak.title"),
      desc: t("arbitrage.infoCards.offPeak.desc"),
      icon: "moon",
      iconColor: ARBITRAGE_COLORS.gold,
    },
    {
      title: t("arbitrage.infoCards.peak.title"),
      desc: t("arbitrage.infoCards.peak.desc"),
      icon: "clock",
      iconColor: ARBITRAGE_COLORS.accent,
    },
  ];

  const backupModes: BackupModeT[] = [
    {
      title: t("backupModes.modes.essential.title"),
      pillLabel: t("backupModes.modes.essential.pillLabel"),
      toneSolid: TONE_SOLID.cyan,
      toneGlow: TONE_GLOW.cyan,
      items: [
        t("backupModes.modes.essential.items.item1"),
        t("backupModes.modes.essential.items.item2"),
        t("backupModes.modes.essential.items.item3"),
        t("backupModes.modes.essential.items.item4"),
      ],
    },
    {
      title: t("backupModes.modes.wholeHome.title"),
      pillLabel: t("backupModes.modes.wholeHome.pillLabel"),
      toneSolid: TONE_SOLID.blue,
      toneGlow: TONE_GLOW.blue,
      items: [
        t("backupModes.modes.wholeHome.items.item1"),
        t("backupModes.modes.wholeHome.items.item2"),
        t("backupModes.modes.wholeHome.items.item3"),
        t("backupModes.modes.wholeHome.items.item4"),
      ],
    },
  ];

  const doNothingCard: OutcomeCardT = {
    title: t("cta.outcomes.doNothing.title"),
    highlighted: false,
    rows: [
      { label: t("cta.outcomes.doNothing.rows.current.label"), value: t("cta.outcomes.doNothing.rows.current.value") },
      { label: t("cta.outcomes.doNothing.rows.tenYear.label"), value: t("cta.outcomes.doNothing.rows.tenYear.value") },
    ],
    noteLabel: t("cta.outcomes.doNothing.noteLabel"),
    noteText: t("cta.outcomes.doNothing.noteText"),
  };

  const takeControlCard: OutcomeCardT = {
    title: t("cta.outcomes.takeControl.title"),
    highlighted: true,
    rows: [
      {
        label: t("cta.outcomes.takeControl.rows.current.label"),
        value: t("cta.outcomes.takeControl.rows.current.value"),
      },
      {
        label: t("cta.outcomes.takeControl.rows.tenYear.label"),
        value: t("cta.outcomes.takeControl.rows.tenYear.value"),
      },
    ],
    noteLabel: t("cta.outcomes.takeControl.noteLabel"),
    noteText: t("cta.outcomes.takeControl.noteText"),
  };

  return (
    <>
      <HeroSection onQuoteOpen={handleQuoteOpen} t={t} />
      <CrisisSection t={t} cards={crisisCards} />
      <SmartResponseSection t={t} />
      {energyProducts.map((product, i) => (
        <EnergySection
          key={product.name}
          product={product}
          index={i}
          seeSavingsLabel={t("products.common.seeSavingsLabel")}
          ctaLabel={t("products.common.ctaLabel")}
          onQuoteOpen={handleQuoteOpen}
        />
      ))}
      <ArbitrageSection t={t} infoCards={arbitrageInfoCards} />
      <BackupModesSection t={t} modes={backupModes} />
      <IncentiveSection t={t} onQuoteOpen={handleQuoteOpen} />
      <CallToActionSection
        t={t}
        doNothingCard={doNothingCard}
        takeControlCard={takeControlCard}
        onQuoteOpen={handleQuoteOpen}
      />
      <QuoteModal open={quoteOpen} zip={quoteZip} onClose={() => setQuoteOpen(false)} />
    </>
  );
};

const EnergyStoragePage = () => {
  const { t } = useTranslation("static_energy_storage");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl="https://powur.com/energy-storage"
      ogTitle={t("meta.ogTitle")}
      ogDescription={t("meta.ogDescription")}
      noTopSpacer
    >
      <EnergyStorageContent />
    </StaticPageLayout>
  );
};

export default EnergyStoragePage;
